import React, { ReactNode, useEffect, useState } from 'react';

import { APP_VERSION, isLocalDev } from 'env';
import { logger } from 'utils';
import { HorizontalLayout, IconAlertTriangle, Text } from 'widgets';

interface VersionProps {
    fallback?: ReactNode;
    compact?: boolean;
    showCurrentVersion?: boolean;
}

export const Version = ({ fallback, compact = false, showCurrentVersion = true }: VersionProps) => {
    const [versionMismatch, setVersionMismatch] = useState(false);

    const checkVersion = async () => {
        try {
            const response = await fetch('/version.json');
            const data = await response.json();
            setVersionMismatch(data.version !== APP_VERSION);
        } catch (error) {
            logger.error('Failed to check version', { error: error });
            setVersionMismatch(false);
        }
    };

    useEffect(() => {
        if (isLocalDev) {
            return;
        }

        window.addEventListener('focus', checkVersion);
        return () => {
            window.removeEventListener('focus', checkVersion);
        };
    }, []);

    return (
        <div className="w-full">
            {versionMismatch ? (
                compact ? (
                    <div className="flex items-center justify-center py-2">
                        <button
                            type="button"
                            className="border-notification-border bg-notification text-notification-foreground hover:border-notification-hover-border hover:bg-notification-hover hover:text-notification-hover-foreground flex h-[33px] w-[33px] cursor-pointer items-center justify-center rounded-full border-2 p-2 transition-colors"
                            onClick={() => window.location.reload()}
                            title="Click to update"
                            aria-label="New version available. Click to update."
                        >
                            <IconAlertTriangle size="xSmall" />
                        </button>
                    </div>
                ) : (
                    <div className="px-6 py-2">
                        <button
                            type="button"
                            className="group border-notification-border bg-notification text-notification-foreground hover:border-notification-hover-border hover:bg-notification-hover hover:text-notification-hover-foreground flex w-full cursor-pointer items-center justify-center gap-4 rounded-full border-2 py-1 transition-colors"
                            onClick={() => window.location.reload()}
                            title="Click to update"
                            aria-label="New version available. Click to update."
                        >
                            <IconAlertTriangle size="xSmall" />
                            <Text helper className="strong">
                                <span className="group-hover:hidden">New version available</span>
                                <span className="hidden group-hover:inline">Click to update</span>
                            </Text>
                        </button>
                    </div>
                )
            ) : fallback ? (
                <>{fallback}</>
            ) : showCurrentVersion ? (
                <HorizontalLayout $center $margin="0 0 8px 0">
                    <Text helper>v{APP_VERSION}</Text>
                </HorizontalLayout>
            ) : null}
        </div>
    );
};
